import type { Icon } from '@phosphor-icons/react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import {
  Archive,
  Bookmark,
  BookmarkSimple,
  Briefcase,
  Buildings,
  Calendar,
  ChartBar,
  Circle,
  Clipboard,
  ClipboardText,
  Clock,
  CreditCard,
  Cube,
  Database,
  File,
  Files,
  FileText,
  Flag,
  Folder,
  FolderOpen,
  FolderStar,
  Gear,
  Gift,
  Globe,
  GridFour,
  Heart,
  Hexagon,
  House,
  Lightbulb,
  Lightning,
  ListBullets,
  ListChecks,
  MapPin,
  Package,
  PencilSimple,
  PushPin,
  Rows,
  Shapes,
  ShoppingCart,
  Sparkle,
  Square,
  Stack,
  StackSimple,
  Star,
  Storefront,
  Table,
  Tag,
  Target,
  Tray,
  Trophy,
  Users,
  Wallet,
} from '@phosphor-icons/react'

// Icon map for dynamic rendering - exported for use in sidebar
export const COLLECTION_ICONS: Record<string, Icon> = {
  Folder,
  FolderOpen,
  FolderStar,
  Files,
  File,
  FileText,
  Database,
  Table,
  ListBullets,
  ListChecks,
  Rows,
  GridFour,
  Stack,
  StackSimple,
  Archive,
  Tray,
  Clipboard,
  ClipboardText,
  Bookmark,
  BookmarkSimple,
  Tag,
  Flag,
  PushPin,
  Star,
  Package,
  Cube,
  Shapes,
  Hexagon,
  Circle,
  Square,
  Lightning,
  Heart,
  Sparkle,
  Globe,
  Users,
  Briefcase,
  ShoppingCart,
  Storefront,
  Buildings,
  House,
  MapPin,
  Calendar,
  Clock,
  ChartBar,
  CreditCard,
  Wallet,
  Gift,
  Trophy,
  Target,
  Lightbulb,
  Gear,
}

// Default icon when none is selected
export const DEFAULT_COLLECTION_ICON = 'ListBullets'

interface CollectionIconPickerProps {
  value?: string | null
  onChange: (icon: string) => void
  initiallyExpanded?: boolean
}

export function CollectionIconPicker({
  value,
  onChange,
  initiallyExpanded = false,
}: CollectionIconPickerProps) {
  const selectedIcon = value || DEFAULT_COLLECTION_ICON
  const SelectedIconComponent = getCollectionIcon(selectedIcon)
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded)

  useEffect(() => {
    setIsExpanded(initiallyExpanded)
  }, [initiallyExpanded])

  const handleSelect = (iconName: string) => {
    onChange(iconName)
    setIsExpanded(false)
  }

  return (
    <div className="space-y-2">
      <Label>Icon</Label>
      {isExpanded ? (
        <div className="space-y-2">
          <div className="grid max-h-[160px] grid-cols-10 gap-1 overflow-y-auto rounded-lg bg-muted/30 p-2">
            {Object.entries(COLLECTION_ICONS).map(
              ([iconName, IconComponent]) => (
                <button
                  key={iconName}
                  type="button"
                  onClick={() => handleSelect(iconName)}
                  className={cn(
                    'flex items-center justify-center rounded-md p-2 transition-colors',
                    'hover:bg-primary/10',
                    selectedIcon === iconName
                      ? 'bg-primary/20 text-primary ring-1 ring-primary/30'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                  title={iconName}
                >
                  <IconComponent className="h-5 w-5" />
                </button>
              ),
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Selected: {selectedIcon}
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-border/60 bg-background px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <SelectedIconComponent className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground">
                  Selected icon
                </p>
                <p className="truncate text-sm font-medium text-foreground">
                  {selectedIcon}
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => setIsExpanded(true)}
            >
              <PencilSimple className="mr-1.5 h-4 w-4" />
              Edit
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// Helper to get icon component by name
export function getCollectionIcon(iconName?: string | null): Icon {
  if (!iconName || !COLLECTION_ICONS[iconName]) {
    return COLLECTION_ICONS[DEFAULT_COLLECTION_ICON]
  }
  return COLLECTION_ICONS[iconName]
}
